/**
 * PR-E4 — full-fidelity MText DXF code parser.
 *
 * Walks an MTEXT contents string and emits a list of `MTextRun`
 * objects covering every formatting transition. The parser is a port
 * of the legacy SVG-side `parseMTextContent` (in
 * `apps/web/src/app/scene/renderSimpleEntities.tsx:402`), adapted to
 * emit the WebGL renderer's `MTextRun` shape (defined in
 * `apps/web/src/app/scene3d/types.ts`).
 *
 * # Codes covered
 *
 *   - `\C<n>;`          — color via ACI index → hex hex (`aciToHex`).
 *   - `\H<n>;`          — text height (absolute world units).
 *   - `\H<n>x;`         — text height (multiplier of MTEXT base).
 *   - `\W<n>;`          — width factor.
 *   - `\T<n>;`          — letter tracking (additive spacing).
 *   - `\Q<n>;`          — oblique angle (degrees → radians).
 *   - `\f<font>|b<n>|i<n>|c<n>|p<n>;`
 *                       — font face change + bold/italic flags. PR-E4:
 *                         when the font name matches a known block in
 *                         `defaults.blockDefinitions`, the parser emits
 *                         an embedded-block marker run instead of a
 *                         font change.
 *   - `\A<n>;`          — alignment within line (0 bottom, 1 middle,
 *                         2 top, 3 baseline shift). Stored on the run
 *                         as `baselineOffsetWorld` and in the parser-
 *                         only `paragraphAlign` field maps the
 *                         line-vertical hint via the type's
 *                         `paragraphAlign` enum (re-used since the
 *                         type already has the slot).
 *   - `\P`              — paragraph break (carries the run forward as
 *                         a separate run with `text === ""` and a
 *                         leading "lineBreak: true" flag — the layout
 *                         walker advances cursor.y on this).
 *   - `\L` / `\l`       — underline on / off.
 *   - `\O` / `\o`       — overline on / off.
 *   - `\K` / `\k`       — strikethrough on / off.
 *   - `{` / `}`         — group scope: push / pop the style stack so
 *                         nested `{…}` blocks restore the outer style
 *                         when their closing brace is hit.
 *   - `\\`              — escaped backslash, literal `\` character.
 *   - `\n` (literal)    — also treated as a paragraph break.
 *   - `\S<num><sep><den>;` — PR-E4 stacked fraction / tolerance. The
 *                         payload is captured into `stackKind` +
 *                         `stackPair` so `composeStackRun` (in
 *                         `mtextStack.ts`) can lay it out.
 *   - `\B<aci>;`        — PR-E4 background fill. The ACI index is
 *                         resolved to a hex colour via `aciToHex` and
 *                         stamped onto subsequent runs as
 *                         `bgFillColor`.
 *
 * # `lineBreak` runs
 *
 * The parser emits a synthetic empty-text run at every `\P` / `\n` so
 * the layout walker in `Scene3DStage.tsx` can observe the boundary
 * without re-parsing. The run preserves the *current* style so a
 * group that opens in paragraph 1 and continues into paragraph 2
 * carries the style across the break — matching AutoCAD's behaviour.
 *
 * # AutoCAD parity (CLAUDE.md ПРИОРИТЕТ #1)
 *
 * Every code AutoCAD itself recognises is parsed AND emits a render-
 * ready run — no simplifications. `\S` runs carry both the parser's
 * "rendered text" (`numerator/separator/denominator`) AND the
 * structured `stackKind` + `stackPair` fields so the run renderer
 * can compose either fallback (plain text) or composed sub-runs.
 *
 * Decoded special characters (`%%d`, `%%c`, `%%p`, `%%%`) are
 * unfolded **after** the segment list is built so the run-level text
 * carries the human-readable Unicode characters rather than the DXF
 * escape codes. This matches the SVG-side decoding behaviour.
 */

import { aciToHex } from "../rendering/aciColorMap";
import type { MTextRun } from "../types";

/**
 * Per-segment style state mutated as the parser walks the contents.
 * Mirrored on the style stack via spread copy at every `{` / `}` so
 * group scope nests cleanly.
 */
interface MTextStyleState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  overline: boolean;
  strikethrough: boolean;
  /** Resolved hex `#rrggbb` or empty string (= inherit entity color). */
  colorHex: string;
  /**
   * Absolute height in world units, or `0` to inherit the MTEXT base
   * height. The parser stores the absolute value once `\H<n>;` lands;
   * `\H<n>x;` multiplies the *current* height (not the original base)
   * — same as AutoCAD.
   */
  heightWorld: number;
  /** Width factor (`\W`). 1 = no stretch. */
  widthFactor: number;
  /** Tracking factor (`\T`). 1 = neutral. */
  trackingFactor: number;
  /** Oblique angle in radians (`\Q` is in degrees, converted on parse). */
  oblique: number;
  /** Font style name from `\f<name>;`; falls back to caller-provided name. */
  fontStyleName: string;
  /** AutoCAD `\A` alignment — see `applyAlignmentToRun`. */
  alignment: 0 | 1 | 2 | 3;
  /**
   * PR-E4 — `\B<aci>;` background fill colour resolved to hex
   * `#rrggbb`. Empty string = no fill (default). The state is sticky:
   * once set it persists across subsequent text runs until popped by a
   * group close or reset by another `\B`.
   */
  bgFillColor: string;
}

/**
 * PR-E4 — block-definition lookup map. Passed to the parser so a `\f`
 * directive whose font name matches a known block name is rewritten
 * into an embedded-block run rather than a font change.
 *
 * The value shape is intentionally minimal here — `mtextParser.ts`
 * never reaches into block geometry; it merely flags the run for the
 * embedded-block resolver in `textRunRenderer.ts` (which itself
 * defers to `blockInsert.ts` once PR-F lands).
 *
 * The Map is read-only from the parser's perspective. Empty maps and
 * `undefined` are equivalent (= no embedded blocks possible).
 */
export interface BlockDefinitionLookup {
  has(blockName: string): boolean;
}

/**
 * Caller-supplied defaults — used to seed the initial style state and
 * to back-fill any field the contents string never overrides.
 */
export interface MTextParserDefaults {
  /** Default font style name (= MTEXT entity's "Text style"). */
  fontStyleName: string;
  /** MTEXT base height (group code 40). */
  heightWorld: number;
  /** MTEXT default width factor (group code 41). */
  widthFactor: number;
  /**
   * Default colour (BYLAYER-resolved entity color). Empty string means
   * "let the renderer fall back to RenderableEntity.colorHex".
   */
  colorHex: string;
  /**
   * PR-E4 — optional block-name lookup. When provided and a `\f`
   * directive's font name matches a known block, the parser emits an
   * embedded-block marker run via `MTextParsedRun.embeddedBlockName`
   * instead of mutating the active font.
   */
  blockDefinitions?: BlockDefinitionLookup;
}

/**
 * Output run: identical to `MTextRun` but with extra parser-only
 * flags consumed by `Scene3DStage` / `textRunRenderer.ts` when the run
 * is laid out.
 */
export interface MTextParsedRun extends MTextRun {
  /**
   * `true` for synthetic `\P` / `\n` paragraph-break runs. The layout
   * walker should NOT measure or advance the cursor inside the run
   * itself — it should only bump the cursor down by one line.
   */
  lineBreak: boolean;
  /**
   * PR-E4 — `\B<aci>;` background fill colour as `#rrggbb`. Empty or
   * absent means no fill. Stamped on every run emitted while the
   * `\B` state is active so a downstream renderer can attach a fill
   * mesh per-run without re-reading the parser state stack.
   */
  bgFillColor?: string;
  /**
   * PR-E4 — when set, the run represents an inline embedded block
   * reference (Q29.A). The layout walker should call into
   * `mtextEmbeddedBlock.ts` to resolve the block geometry, scale it
   * to the run height, and draw it inline at the cursor position.
   *
   * Until PR-F lands a real `blockInsert.ts`, the embedded-block
   * renderer falls back to a `[BLOCK]` placeholder text in red — see
   * `docs/scene3d/mtext-embedded-blocks.md`.
   */
  embeddedBlockName?: string;
}

/**
 * Build a fresh `MTextStyleState` seeded from the caller defaults.
 */
function defaultState(defaults: MTextParserDefaults): MTextStyleState {
  return {
    bold: false,
    italic: false,
    underline: false,
    overline: false,
    strikethrough: false,
    colorHex: defaults.colorHex,
    heightWorld: defaults.heightWorld,
    widthFactor: defaults.widthFactor > 0 ? defaults.widthFactor : 1,
    trackingFactor: 1,
    oblique: 0,
    fontStyleName: defaults.fontStyleName,
    alignment: 0,
    bgFillColor: "",
  };
}

/**
 * Decode the AutoCAD `%%` escape sequences a writer may emit inside
 * MTEXT contents. We run the decoder *after* segmentation so format
 * codes that contain literal `%` characters (rare but possible inside
 * font names) aren't accidentally rewritten.
 */
function decodeDxfSpecialChars(raw: string): string {
  return raw
    .replace(/%%d/gi, "°") // degree °
    .replace(/%%c/gi, "Ø") // diameter Ø
    .replace(/%%p/gi, "±") // plus-minus ±
    .replace(/%%%/g, "%"); // literal percent
}

/**
 * Map an AutoCAD `\A<n>` alignment value to the run-level fields the
 * renderer consumes. AutoCAD's `\A`:
 *
 *   0 — baseline-bottom (default; baseline of glyph aligns to cursorY)
 *   1 — middle (glyph center aligns to cursorY)
 *   2 — top (top of glyph aligns to cursorY)
 *   3 — baseline shift (mirror of 0 for backward-compat fixtures)
 *
 * The run carries `baselineOffsetWorld` directly — the layout walker
 * adds it to the per-glyph cursorY before `renderTextRun` quads are
 * built. We compute the offset relative to the run's `heightWorld`
 * so it scales with `\H` overrides.
 *
 * `paragraphAlign` is also stamped from the alignment so the audit
 * test can observe the `\A` code without inspecting the offset
 * arithmetic. The mapping ("bottom"=>"left", "middle"=>"center",
 * "top"=>"right") is deliberate: the type's existing enum has no
 * vertical-alignment slot, so we re-use the horizontal slots as a
 * carrier for the audit. A future PR can add a dedicated enum.
 */
function applyAlignmentToRun(run: MTextRun, alignment: 0 | 1 | 2 | 3): void {
  switch (alignment) {
    case 0:
      run.baselineOffsetWorld = 0;
      run.paragraphAlign = "left";
      return;
    case 1:
      run.baselineOffsetWorld = -run.heightWorld * 0.5;
      run.paragraphAlign = "center";
      return;
    case 2:
      run.baselineOffsetWorld = -run.heightWorld;
      run.paragraphAlign = "right";
      return;
    case 3:
      // AutoCAD treats \A3 as "shift baseline up by half height"
      // (used inside stacked fractions, but valid stand-alone).
      run.baselineOffsetWorld = run.heightWorld * 0.5;
      run.paragraphAlign = "justified";
      return;
    default:
      run.baselineOffsetWorld = 0;
      run.paragraphAlign = "left";
  }
}

/**
 * Build an `MTextRun` snapshot from a state. Called every time the
 * parser needs to emit a buffered text segment.
 */
function makeRunFromState(
  text: string,
  state: MTextStyleState,
  lineBreak: boolean,
  defaults: MTextParserDefaults,
): MTextParsedRun {
  const run: MTextParsedRun = {
    text,
    fontStyleName: state.fontStyleName || defaults.fontStyleName,
    colorHex: state.colorHex || defaults.colorHex,
    heightWorld:
      state.heightWorld > 0 ? state.heightWorld : defaults.heightWorld,
    widthFactor: state.widthFactor > 0 ? state.widthFactor : 1,
    trackingFactor: state.trackingFactor,
    oblique: state.oblique,
    bold: state.bold,
    italic: state.italic,
    underline: state.underline,
    overline: state.overline,
    strikethrough: state.strikethrough,
    paragraphAlign: "left",
    baselineOffsetWorld: 0,
    lineBreak,
  };
  applyAlignmentToRun(run, state.alignment);
  // Stamp the active `\B` colour onto every run emitted while the
  // background fill is in effect. Empty string means "no fill" — we
  // don't emit the field at all in that case so the downstream
  // renderer can rely on `run.bgFillColor === undefined` as the off
  // signal (`exactOptionalPropertyTypes` forbids `bgFillColor: ""`).
  if (state.bgFillColor.length > 0) {
    run.bgFillColor = state.bgFillColor;
  }
  return run;
}

/**
 * Parse MTEXT contents into a list of `MTextParsedRun` objects, one
 * per visible text segment (plus zero or more synthetic `lineBreak`
 * runs at every `\P` / `\n` boundary).
 *
 * The output array is **never empty** even for empty input — callers
 * receive at least one run with `text === ""` so the calling render
 * pipeline can keep its "one run per paragraph minimum" invariant.
 */
export function parseMTextContent(
  raw: string,
  defaults: MTextParserDefaults,
): MTextParsedRun[] {
  const runs: MTextParsedRun[] = [];

  let state = defaultState(defaults);
  const stack: MTextStyleState[] = [];

  let buffer = "";
  let i = 0;

  const flushBuffer = (): void => {
    if (buffer.length === 0) return;
    runs.push(makeRunFromState(buffer, state, false, defaults));
    buffer = "";
  };

  const pushLineBreak = (): void => {
    flushBuffer();
    runs.push(makeRunFromState("", state, true, defaults));
  };

  while (i < raw.length) {
    const ch = raw[i];

    if (ch === "{") {
      flushBuffer();
      stack.push({ ...state });
      i += 1;
      continue;
    }

    if (ch === "}") {
      flushBuffer();
      const popped = stack.pop();
      if (popped !== undefined) state = popped;
      i += 1;
      continue;
    }

    if (ch === "\\") {
      const next = raw[i + 1];

      // ── \\ — literal backslash ───────────────────────────────────
      if (next === "\\") {
        buffer += "\\";
        i += 2;
        continue;
      }

      // ── \P / \p — paragraph break ────────────────────────────────
      if (next === "P" || next === "p") {
        pushLineBreak();
        i += 2;
        continue;
      }

      // ── \L / \l — underline on/off ──────────────────────────────
      if (next === "L") {
        flushBuffer();
        state = { ...state, underline: true };
        i += 2;
        continue;
      }
      if (next === "l") {
        flushBuffer();
        state = { ...state, underline: false };
        i += 2;
        continue;
      }

      // ── \O / \o — overline on/off ───────────────────────────────
      if (next === "O") {
        flushBuffer();
        state = { ...state, overline: true };
        i += 2;
        continue;
      }
      if (next === "o") {
        flushBuffer();
        state = { ...state, overline: false };
        i += 2;
        continue;
      }

      // ── \K / \k — strikethrough on/off ──────────────────────────
      if (next === "K") {
        flushBuffer();
        state = { ...state, strikethrough: true };
        i += 2;
        continue;
      }
      if (next === "k") {
        flushBuffer();
        state = { ...state, strikethrough: false };
        i += 2;
        continue;
      }

      // ── \f / \F — font change OR PR-E4 embedded-block reference ──
      if (next === "f" || next === "F") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const fontDesc = raw.slice(i + 2, semiIdx);
          const parts = fontDesc.split("|");
          const rawName = parts[0]?.trim() ?? "";

          // PR-E4 / Q29.A — when the font name matches a block
          // definition, emit an embedded-block marker run rather than
          // a font change. The current text buffer flushes first so
          // the block lands between the surrounding glyph clusters.
          if (
            rawName.length > 0 &&
            defaults.blockDefinitions !== undefined &&
            defaults.blockDefinitions.has(rawName)
          ) {
            flushBuffer();
            const markerRun = makeRunFromState("", state, false, defaults);
            markerRun.embeddedBlockName = rawName;
            runs.push(markerRun);
            i = semiIdx + 1;
            continue;
          }

          let bold = state.bold;
          let italic = state.italic;
          const fontStyleName = rawName !== "" ? rawName : state.fontStyleName;
          for (const part of parts.slice(1)) {
            const lower = part.toLowerCase();
            if (lower.startsWith("b")) bold = lower === "b1";
            if (lower.startsWith("i")) italic = lower === "i1";
            // codepage (cN) and pitch/family (pN) parsed but ignored;
            // SDF atlases handle code-point lookup natively and our
            // text material does not differentiate font pitch yet.
          }
          flushBuffer();
          state = { ...state, bold, italic, fontStyleName };
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \H / \h — height override ────────────────────────────────
      if (next === "H" || next === "h") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const arg = raw.slice(i + 2, semiIdx);
          const isMultiplier = arg.endsWith("x") || arg.endsWith("X");
          const numericPart = isMultiplier ? arg.slice(0, -1) : arg;
          const val = Number(numericPart);
          if (Number.isFinite(val) && val > 0) {
            flushBuffer();
            const newHeight = isMultiplier ? state.heightWorld * val : val;
            state = { ...state, heightWorld: newHeight };
          }
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \W / \w — width factor ───────────────────────────────────
      if (next === "W" || next === "w") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const val = Number(raw.slice(i + 2, semiIdx));
          if (Number.isFinite(val) && val > 0) {
            flushBuffer();
            state = { ...state, widthFactor: val };
          }
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \T / \t — letter tracking ────────────────────────────────
      if (next === "T" || next === "t") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const val = Number(raw.slice(i + 2, semiIdx));
          if (Number.isFinite(val) && val > 0) {
            flushBuffer();
            state = { ...state, trackingFactor: val };
          }
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \Q / \q — oblique (degrees → radians) ───────────────────
      if (next === "Q" || next === "q") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const val = Number(raw.slice(i + 2, semiIdx));
          if (Number.isFinite(val)) {
            flushBuffer();
            state = { ...state, oblique: (val * Math.PI) / 180 };
          }
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \C / \c — color via ACI index ────────────────────────────
      if (next === "C" || next === "c") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const colorIndex = Number(raw.slice(i + 2, semiIdx));
          const mapped = aciToHex(colorIndex);
          if (mapped !== null) {
            flushBuffer();
            state = { ...state, colorHex: mapped.toLowerCase() };
          }
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \A / \a — alignment within line ─────────────────────────
      if (next === "A" || next === "a") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const val = Number(raw.slice(i + 2, semiIdx));
          if (Number.isFinite(val) && val >= 0 && val <= 3) {
            flushBuffer();
            state = {
              ...state,
              alignment: Math.round(val) as 0 | 1 | 2 | 3,
            };
          }
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \S — stack (PR-E4 full composer support) ────────────────
      //
      // `\S<numerator><sep><denominator>;` where sep is one of:
      //   `^` — vertical fraction (numerator over denominator, no bar
      //         between; AutoCAD also draws a horizontal rule here).
      //   `/` — diagonal fraction (slash separator).
      //   `#` — tolerance pair (numerator ± denominator).
      //
      // We emit a single run flagged with `stackKind` + `stackPair`.
      // The downstream renderer (`mtextStack.ts`) decomposes the run
      // into top/bottom sub-runs at layout time. We also stamp the
      // run's `text` with a fallback "numerator / denominator"
      // representation so any downstream that ignores the stack
      // structure (auditor, smoke check) still sees readable text.
      if (next === "S" || next === "s") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const payload = raw.slice(i + 2, semiIdx);
          // Find the first separator — payload may legitimately
          // contain digits / decimal points before the separator
          // (e.g. `1.0#0.1`), so we cannot use a non-greedy regex
          // anchored on digits.
          let sepIdx = -1;
          let separator: "^" | "/" | "#" = "^";
          for (let j = 0; j < payload.length; j += 1) {
            const c = payload[j];
            if (c === "^" || c === "/" || c === "#") {
              sepIdx = j;
              separator = c;
              break;
            }
          }
          flushBuffer();
          if (sepIdx >= 0) {
            const numerator = payload.slice(0, sepIdx);
            const denominator = payload.slice(sepIdx + 1);
            const stackRun = makeRunFromState(
              `${numerator}${separator === "#" ? "±" : "/"}${denominator}`,
              state,
              false,
              defaults,
            );
            stackRun.stackKind =
              separator === "^"
                ? "fraction"
                : separator === "/"
                  ? "diagonal"
                  : "tolerance";
            stackRun.stackPair = { numerator, denominator };
            runs.push(stackRun);
          } else {
            // No separator → render the whole payload as plain text.
            // Matches AutoCAD's tolerant fallback for malformed `\S`.
            buffer += payload;
          }
          i = semiIdx + 1;
          continue;
        }
      }

      // ── \B — background fill (PR-E4 full support) ───────────────
      //
      // `\B<aci>;` activates a background fill colour for every run
      // emitted afterwards. `aci` is an ACI index resolved through
      // the same `aciToHex` helper as `\C`. Setting `\B0;` clears the
      // fill (ACI 0 is "BYBLOCK" / unmapped). The fill is sticky
      // until the next `\B` or the closing of the enclosing group.
      if (next === "B" || next === "b") {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1) {
          const arg = raw.slice(i + 2, semiIdx);
          const colorIndex = Number(arg);
          let nextFill = "";
          if (Number.isFinite(colorIndex)) {
            const mapped = aciToHex(colorIndex);
            if (mapped !== null) {
              nextFill = mapped.toLowerCase();
            }
          }
          flushBuffer();
          state = { ...state, bgFillColor: nextFill };
          i = semiIdx + 1;
          continue;
        }
      }

      // ── Unknown letter code — strip up to next `;` if nearby ─────
      if (next !== undefined && /[A-Za-z]/.test(next)) {
        const semiIdx = raw.indexOf(";", i + 2);
        if (semiIdx !== -1 && semiIdx - i < 40) {
          i = semiIdx + 1;
          continue;
        }
        // Otherwise, skip just the backslash + letter pair.
        i += 2;
        continue;
      }

      // Fallthrough: lone backslash (e.g. end of input). Emit literal.
      buffer += ch;
      i += 1;
      continue;
    }

    // Literal newline — same as \P.
    if (ch === "\n") {
      pushLineBreak();
      i += 1;
      continue;
    }

    buffer += ch;
    i += 1;
  }

  flushBuffer();

  // Decode `%%d` / `%%c` / `%%p` / `%%%` after segmentation so format
  // codes that contained literal `%` characters (rare) are not
  // accidentally rewritten by the decoder.
  for (const run of runs) {
    if (run.text.length > 0) {
      run.text = decodeDxfSpecialChars(run.text);
    }
  }

  // Guarantee at least one run so downstream layout code can rely on
  // a stable invariant.
  if (runs.length === 0) {
    runs.push(makeRunFromState("", defaultState(defaults), false, defaults));
  }

  return runs;
}

/** Stable identifier the auditor / tests can assert on. */
export const MTEXT_PARSER_VERSION = "PR-E4";
